import { useState } from "react";

export interface LessonSection {
  heading: string;
  paragraphs: string[];
  keyPoints?: string[];
  codeExample?: { language: string; code: string };
  callout?: { type: "info" | "warning" | "tip"; text: string };
}

export interface LessonContent {
  title: string;
  overview: string;
  sections: LessonSection[];
  keyTakeaways: string[];
}

interface LessonViewerProps {
  content: LessonContent;
  isCompleted?: boolean;
  onComplete?: () => void;
}

export function LessonViewer({ content, isCompleted, onComplete }: LessonViewerProps) {
  const [readSections, setReadSections] = useState<Set<number>>(new Set());
  const [showCompletion, setShowCompletion] = useState(false);

  function markRead(idx: number) {
    setReadSections((prev) => new Set([...prev, idx]));
  }

  const allRead = readSections.size >= content.sections.length;

  function handleComplete() {
    setShowCompletion(true);
    onComplete?.();
  }

  return (
    <div style={{ maxWidth: "720px", margin: "0 auto" }}>
      {/* Overview */}
      <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)" }}>
        <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>{content.title}</h3>
        <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: 1.6 }}>{content.overview}</p>
      </div>

      {/* Sections */}
      {content.sections.map((section, idx) => {
        const isRead = readSections.has(idx);
        return (
          <div
            key={idx}
            className="card"
            style={{
              padding: "var(--space-4)",
              marginBottom: "var(--space-3)",
              borderLeft: isRead ? "3px solid var(--accent-500)" : "3px solid transparent",
              transition: "border-color var(--transition-fast)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
              <h4 style={{ fontSize: "0.9375rem", fontWeight: 700 }}>{idx + 1}. {section.heading}</h4>
              {isRead && <span style={{ color: "var(--accent-500)", fontSize: "0.875rem" }}>&#10003;</span>}
            </div>

            {section.paragraphs.map((p, pi) => (
              <p key={pi} style={{ fontSize: "0.875rem", lineHeight: 1.7, marginBottom: "0.625rem", color: "var(--text)" }}>
                {p}
              </p>
            ))}

            {section.keyPoints && (
              <div style={{ margin: "0.625rem 0", padding: "0.75rem", borderRadius: "var(--radius-md)", background: "var(--bg-subtle)" }}>
                <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.375rem" }}>Key Points</div>
                <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                  {section.keyPoints.map((kp, ki) => (
                    <li key={ki} style={{ fontSize: "0.8125rem", lineHeight: 1.6, marginBottom: "0.25rem", color: "var(--text)" }}>{kp}</li>
                  ))}
                </ul>
              </div>
            )}

            {section.callout && (
              <div
                style={{
                  margin: "0.625rem 0",
                  padding: "0.75rem",
                  borderRadius: "var(--radius-md)",
                  border: `1px solid var(--${section.callout.type === "warning" ? "warning" : section.callout.type === "tip" ? "accent" : "primary"}-300)`,
                  background: `var(--${section.callout.type === "warning" ? "warning" : section.callout.type === "tip" ? "accent" : "primary"}-50)`,
                  display: "flex",
                  gap: "0.5rem",
                }}
              >
                <span style={{ fontSize: "1rem" }}>
                  {section.callout.type === "warning" ? "\u26A0" : section.callout.type === "tip" ? "\u2605" : "\u2139"}
                </span>
                <p style={{ fontSize: "0.8125rem", lineHeight: 1.6, margin: 0, color: "var(--text)" }}>
                  {section.callout.text}
                </p>
              </div>
            )}

            {section.codeExample && (
              <div style={{ margin: "0.625rem 0", borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border)" }}>
                <div style={{ padding: "0.375rem 0.75rem", background: "var(--neutral-100)", fontSize: "0.6875rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {section.codeExample.language}
                </div>
                <pre style={{ margin: 0, padding: "0.75rem", background: "var(--neutral-950)", color: "var(--neutral-100)", fontSize: "0.8125rem", lineHeight: 1.6, overflowX: "auto" }}>
                  <code>{section.codeExample.code}</code>
                </pre>
              </div>
            )}

            {!isRead && (
              <button
                onClick={() => markRead(idx)}
                style={{
                  marginTop: "0.5rem",
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "0.25rem 0.625rem",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  color: "var(--text-muted)",
                }}
              >
                Mark as read
              </button>
            )}
          </div>
        );
      })}

      {/* Key Takeaways */}
      <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)", background: "var(--accent-50)", borderColor: "var(--accent-300)" }}>
        <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: "0.5rem" }}>Key Takeaways</h4>
        <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
          {content.keyTakeaways.map((kt, ki) => (
            <li key={ki} style={{ fontSize: "0.8125rem", lineHeight: 1.6, marginBottom: "0.25rem", color: "var(--text)" }}>{kt}</li>
          ))}
        </ul>
      </div>

      {/* Complete button */}
      {onComplete && (
        showCompletion || isCompleted ? (
          <div style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--accent-50)", border: "1px solid var(--accent-300)", textAlign: "center" }}>
            <div style={{ fontSize: "1.25rem", color: "var(--accent-600)", fontWeight: 700, marginBottom: "0.25rem" }}>&#10003; Lesson Complete</div>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", margin: 0 }}>Your progress has been saved. Continue to the next lesson or take the quiz.</p>
          </div>
        ) : (
          <button
            onClick={handleComplete}
            disabled={!allRead}
            className="btn btn-primary"
            style={{ width: "100%", opacity: allRead ? 1 : 0.5 }}
          >
            {allRead ? "Complete Lesson" : `Read all sections (${readSections.size}/${content.sections.length})`}
          </button>
        )
      )}
    </div>
  );
}
